import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useHubContext } from '@atlassian/company-hub-common';
import {
	type HubBreadcrumbItem,
	useRegisterHubBreadcrumbs,
} from '@atlassian/company-hub-common/hub-breadcrumbs-context';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';

import { staticHubBreadcrumbMessages } from '../static-hub-breadcrumbs/messages';

export interface HubBreadcrumbDefinition {
	href: string;
	id: string;
	text: string | null | undefined;
}

export const getHubRouteHref = (relativeBaseUrl: string, routePath: string): string => {
	const basePath = relativeBaseUrl.replace(/\/+$/, '');
	if (/^https?:\/\//.test(routePath)) {
		return routePath;
	}
	if (basePath && (routePath === basePath || routePath.startsWith(`${basePath}/`))) {
		return routePath;
	}
	const path = routePath === '/' ? '' : `/${routePath.replace(/^\/+/, '')}`;

	return `${basePath}${path}` || '/';
};

export const getHubBreadcrumbItems = ({
	currentItem,
	homeText,
	relativeBaseUrl,
	ancestorItems = [],
}: {
	ancestorItems?: readonly HubBreadcrumbDefinition[];
	currentItem: HubBreadcrumbDefinition | undefined;
	homeText: string;
	relativeBaseUrl: string;
}): readonly HubBreadcrumbItem[] => {
	if (!currentItem) {
		return [];
	}

	const normalizedHierarchy = [...ancestorItems, currentItem].map((item) => ({
		...item,
		text: item.text?.trim(),
	}));

	if (normalizedHierarchy.some((item) => !item.text)) {
		return [];
	}

	return [
		{
			href: getHubRouteHref(relativeBaseUrl, '/'),
			id: 'home',
			text: homeText,
		},
		...normalizedHierarchy.map((item) => ({
			href: getHubRouteHref(relativeBaseUrl, item.href),
			id: item.id,
			text: item.text ?? '',
		})),
	];
};

export const useAreHubBreadcrumbsEnabled = (): boolean => {
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);

	return isBreadcrumbsEnabled;
};

export const useHubBreadcrumbs = ({
	ancestorItems,
	currentItem,
}: {
	ancestorItems?: readonly HubBreadcrumbDefinition[];
	currentItem: HubBreadcrumbDefinition | undefined;
}): void => {
	const { formatMessage } = useIntl();
	const {
		env: { relativeBaseUrl },
	} = useHubContext();
	const isBreadcrumbsEnabled = useAreHubBreadcrumbsEnabled();

	const items = useMemo<readonly HubBreadcrumbItem[]>(() => {
		if (!isBreadcrumbsEnabled) {
			return [];
		}

		return getHubBreadcrumbItems({
			ancestorItems,
			currentItem,
			relativeBaseUrl,
			homeText: formatMessage(staticHubBreadcrumbMessages.home),
		});
	}, [ancestorItems, currentItem, formatMessage, isBreadcrumbsEnabled, relativeBaseUrl]);

	useRegisterHubBreadcrumbs(items);
};
